import { SEMAPHORE_WITH_NEUTRAL, type SemaphoreToneWithNeutral } from "@dd-report-components/tokens";
import styled from "styled-components";
import { Badge } from "../../../Components/Badge";

export const CasesListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  height: 100%;
  width: 100%;
  padding: 1.5rem 0;
  background-color: transparent;
  gap: 1.5rem;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding: 1rem 0;
  }
`;

export const CasesListTitleSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  gap: 1rem;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1.5rem;
  }
`;

export const CasesListTitleInner = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

export const CasesListPageTitle = styled.h1`
  font-size: 1.875rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
  padding: 0;
  line-height: 1.2;
  letter-spacing: -0.025em;
`;

export const CasesListPageSubtitle = styled.p`
  color: #6B7280;
  margin: 0.5rem 0 0 0;
  padding: 0;
  font-size: 1rem;
  line-height: 1.5;
`;

export const CasesListHeaderActions = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
    width: 100%;
  }
`;

/* Credit balance: the bar fills with what's left, in the semaphore's tone */
export const CreditBalanceWrapper = styled.div<{ $tone: SemaphoreToneWithNeutral }>`
  --credit-balance-tone: ${(props: { $tone: SemaphoreToneWithNeutral }) => SEMAPHORE_WITH_NEUTRAL[props.$tone].fill};
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
  min-width: 10rem;
  padding-right: 1.25rem;
  margin-right: 1.25rem;
  border-right: 1px solid ${(props) => props.theme.colors.border};

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    align-items: flex-start;
    padding-right: 0;
    margin-right: 0;
    border-right: none;
  }
`;

export const CreditBalanceValue = styled.div`
  font-size: ${(props) => props.theme.fontSizes.small};
  font-weight: 600;
  color: ${(props) => props.theme.colors.font.primary};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

export const CreditBalanceCap = styled.span`
  margin-left: 0.25rem;
  font-weight: 400;
`;

export const CreditBalanceBar = styled.div<{ $percent: number }>`
  width: 100%;
  height: 3px;
  border-radius: 9999px;
  background: linear-gradient(
    to right,
    var(--credit-balance-tone) ${(props) => props.$percent}%,
    ${(props) => props.theme.colors.border} ${(props) => props.$percent}%
  );
`;

export const CreditBalanceFooter = styled.div`
  font-size: ${(props) => props.theme.fontSizes.xsmall};
  color: ${(props) => props.theme.colors.font.grey2};
  white-space: nowrap;
`;

export const CreditBalanceLink = styled.button`
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-weight: 500;
  color: var(--credit-balance-tone);
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export const CasesListHeaderButton = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    background-color: #000000;
    color: #ffffff;
    padding: 0.75rem 1.5rem;
    border-radius: 0.75rem;
    border: none;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);

    &:hover {
      background-color: #1F2937;
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
      transform: translateY(-1px);
    }

    svg {
      font-size: 1.25rem;
    }
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    width: 100%;

    button {
      width: 100%;
    }
  }
`;

export const CasesListContent = styled.div`
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  background-color: #ffffff;
  border-radius: 1rem;
  border: 1px solid #E5E7EB;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  overflow: hidden;
  position: relative;
`;

export const SearchAndFiltersWrapper = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #E5E7EB;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const SearchInputWrapper = styled.div`
  position: relative;
  flex: 1;

  .search-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #9CA3AF;
    pointer-events: none;
  }
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.75rem;
  border: 1px solid #E5E7EB;
  border-radius: 0.75rem;
  background-color: #F9FAFB;
  font-size: 0.875rem;
  color: #111827;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #021526;
    background-color: #ffffff;
    box-shadow: 0 0 0 3px rgba(2, 21, 38, 0.1);
  }

  &:disabled {
    background-color: #F3F4F6;
    color: #9CA3AF;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #9CA3AF;
  }
`;

export const FiltersContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
  flex-shrink: 0;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    flex-wrap: wrap;
    width: 100%;
    overflow-x: auto;
    padding-bottom: 0.5rem;
  }

  .filter-label {
    font-size: 0.75rem;
    font-weight: 500;
    color: #6B7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-right: 0.25rem;
    white-space: nowrap;

    @media (max-width: ${(props) => props.theme.breakpoints.md}) {
      display: none;
    }
  }
`;

export const FilterButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid #E5E7EB;
  background-color: ${(props) => (props.$active ? "#021526" : "#ffffff")};
  color: ${(props) => (props.$active ? "#ffffff" : "#4B5563")};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background-color: ${(props) => (props.$active ? "#021526" : "#F9FAFB")};
    color: ${(props) => (props.$active ? "#ffffff" : "#021526")};
    border-color: ${(props) => (props.$active ? "#021526" : "#D1D5DB")};
  }

  svg {
    font-size: 1rem;
  }
`;

export const TableWrapper = styled.div`
  overflow-x: auto;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

export const TableHead = styled.thead`
  background-color: #F9FAFB;
`;

export const TableHeadRow = styled.tr``;

export const TableHeaderCell = styled.th`
  padding: 1.25rem 1.5rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #F3F4F6;

  &.sortable {
    cursor: pointer;
    user-select: none;
    transition: color 0.2s;

    &:hover {
      color: #374151;
    }

    .sort-icon {
      opacity: 0.4;
      transition: opacity 0.2s;
      display: inline-block;
    }

    &:hover .sort-icon {
      opacity: 1;
    }
  }

  &.actions {
    text-align: right;
    padding-right: 2rem;
    cursor: default;
  }
`;

export const TableBody = styled.tbody`
  background-color: #ffffff;
`;

export const TableRow = styled.tr`
  border-bottom: 1px solid #F3F4F6;
  transition: background-color 0.2s;

  &:hover {
    background-color: #F9FAFB;
  }

  &.empty-row {
    height: 12rem;

    td {
      text-align: center;
      padding: 3rem 1.5rem;
      color: #9CA3AF;
      background-color: #F9FAFB;
    }
  }
`;

export const TableCell = styled.td`
  padding: 1.25rem 1.5rem;
  font-size: 0.875rem;
  color: #111827;

  &.actions {
    text-align: right;
    padding-right: 2rem;
  }
`;

export const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

export const Avatar = styled.div<{ color?: string }>`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.5rem;
  background-color: ${(props) => {
    const colors: Record<string, string> = {
      blue: '#DBEAFE',
      indigo: '#E0E7FF',
      purple: '#EDE9FE',
      pink: '#FCE7F3',
      red: '#FEE2E2',
      orange: '#FED7AA',
      yellow: '#FEF3C7',
      green: '#D1FAE5',
    };
    return colors[props.color || 'blue'] || '#DBEAFE';
  }};
  color: ${(props) => {
    const colors: Record<string, string> = {
      blue: '#2563EB',
      indigo: '#4F46E5',
      purple: '#7C3AED',
      pink: '#DB2777',
      red: '#DC2626',
      orange: '#EA580C',
      yellow: '#D97706',
      green: '#059669',
    };
    return colors[props.color || 'blue'] || '#2563EB';
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
  flex-shrink: 0;
`;

export const NameText = styled.span`
  font-weight: 600;
  color: #111827;
`;

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  draft: { bg: '#F3F4F6', color: '#6B7280' },
  pending_payment: { bg: '#FEF3C7', color: '#92400E' },
  in_progress: { bg: '#DBEAFE', color: '#1E40AF' },
  under_review: { bg: '#E0E7FF', color: '#4338CA' },
  completed: { bg: '#D1FAE5', color: '#065F46' },
  archived: { bg: '#F3F4F6', color: '#6B7280' },
  cancelled: { bg: '#FEE2E2', color: '#991B1B' },
};

export const StatusBadge = styled(Badge)<{ $status: string }>`
  ${({ $status }: { $status: string }) => {
    const colors = STATUS_COLORS[$status.toLowerCase()] || { bg: '#F3F4F6', color: '#6B7280' };
    return `
      background-color: ${colors.bg};
      color: ${colors.color};
    `;
  }}
`;

export const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  border-radius: 0.5rem;
  border: none;
  background-color: transparent;
  color: #4B5563;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: #021526;
    background-color: #F3F4F6;
  }

  svg {
    font-size: 1.1rem;
  }
`;

export const EmptyStateRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-center;
  padding: 3rem 1.5rem;
  opacity: 0.4;

  svg {
    font-size: 3rem;
    margin-bottom: 0.5rem;
    color: #9CA3AF;
  }

  span {
    font-size: 0.75rem;
    color: #9CA3AF;
  }
`;

export const PaginationWrapper = styled.div`
  padding: 1rem 1.5rem;
  border-top: none;
  background-color: #F9FAFB;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (min-width: ${(props) => props.theme.breakpoints.md}) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

export const PaginationInfo = styled.div`
  font-size: 0.875rem;
  color: #6B7280;

  .number {
    font-weight: 500;
    color: #111827;
  }
`;

export const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

export const RowsPerPage = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #6B7280;

  select {
    appearance: none;
    padding: 0.375rem 2rem 0.375rem 0.75rem;
    background-color: #ffffff;
    border: 1px solid #D1D5DB;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

    &:hover {
      border-color: #9CA3AF;
    }

    &:focus {
      outline: none;
      border-color: #021526;
      box-shadow: 0 0 0 3px rgba(2, 21, 38, 0.1);
    }
  }
`;

export const PaginationButtons = styled.nav`
  display: inline-flex;
  border-radius: 0.375rem;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

  button {
    position: relative;
    display: inline-flex;
    align-items: center;
    padding: 0.5rem;
    border: 1px solid #D1D5DB;
    background-color: #ffffff;
    color: #9CA3AF;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s;

    &:first-child {
      border-top-left-radius: 0.375rem;
      border-bottom-left-radius: 0.375rem;
      border-right: none;
    }

    &:last-child {
      border-top-right-radius: 0.375rem;
      border-bottom-right-radius: 0.375rem;
      border-left: none;
    }

    &:hover:not(:disabled) {
      background-color: #F9FAFB;
      color: #374151;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &.active {
      background-color: #021526;
      color: #ffffff;
      border-color: #021526;
      z-index: 10;
    }

    svg {
      font-size: 1.125rem;
    }
  }
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: #6B7280;
  text-align: center;
  min-height: 400px;
  gap: 1rem;
`;

export const EmptyStateIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
  line-height: 1;
  color: #9CA3AF;
  opacity: 0.6;

  svg {
    width: 100%;
    height: 100%;
  }
`;

export const EmptyStateTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
  margin-bottom: 0.5rem;
`;

export const EmptyStateDescription = styled.p`
  font-size: 0.875rem;
  color: #6B7280;
  margin: 0;
  margin-bottom: 1.5rem;
  max-width: 500px;
  line-height: 1.5;
`;
